import type { ChatMessage, PeerRecord, SkyChatSocketClient } from '@skychat/shared-core';
import type { Socket } from 'socket.io-client';

export const CHAT_P2P_FILE_THRESHOLD = 50 * 1024 * 1024;

const CHANNEL_LABEL = 'skychat-chat-p2p-file';
const CHUNK_SIZE = 256 * 1024;
const CONNECT_TIMEOUT_MS = 15000;
const ACK_TIMEOUT_MS = 12000;
const FINAL_DONE_TIMEOUT_MS = 5 * 60 * 1000;
const BUFFERED_AMOUNT_HIGH = 8 * 1024 * 1024;
const BUFFERED_AMOUNT_LOW = 2 * 1024 * 1024;

export type ChatP2PSession = {
  sessionId: string;
  messageId: string;
  fromId: string;
  toId: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  iv: string;
  status?: string;
  iceServers?: RTCIceServer[];
};

export type ChatP2PRecord = {
  direction: 'outgoing' | 'incoming';
  session?: ChatP2PSession;
  sessionId?: string;
  peerId: string;
  fromId: string;
  toId: string;
  messageId: string;
  messageTs?: number;
  file?: File;
  cipherBlob?: Blob;
  fileName: string;
  fileSize: number;
  fileType: string;
  iv: string;
  iceServers?: RTCIceServer[];
  pc?: RTCPeerConnection;
  channel?: RTCDataChannel;
  chunks?: ArrayBuffer[];
  receivedBytes?: number;
  receivedDone?: boolean;
  doneConfirmed?: boolean;
  waitingReceiverDone?: boolean;
  encryptedSize?: number;
  totalBytes?: number;
  bytesTransferred?: number;
  speedBps?: number;
  speedSampleAt?: number;
  speedSampleBytes?: number;
  startedAt?: number;
  phase?: 'waiting' | 'connecting' | 'transferring' | 'finalizing' | 'done' | 'failed' | 'cancelled';
  terminal?: boolean;
  connectTimer?: number;
  doneTimer?: number;
};

type ManagerOptions = {
  getSocket(): SkyChatSocketClient | null;
  getPeer(peerId: string): PeerRecord | undefined;
  getMeId(): string;
  t(key: string, fallback?: string): string;
  notify(message: string): void;
  appendOrPatchMessage(peerId: string, message: ChatMessage): void;
  patchMessage(peerId: string, messageId: string, patch: Partial<ChatMessage> & Record<string, unknown>): void;
  saveReceivedBlob(message: ChatMessage, peerId: string, blob: Blob): void;
  decryptIncomingBlob(record: ChatP2PRecord, encryptedBlob: Blob): Promise<Blob>;
  sendChatMessage(message: ChatMessage): void;
};

function sessionOf(payload: unknown): ChatP2PSession | null {
  const outer = (payload || {}) as Record<string, unknown>;
  const raw = ((outer.session || outer.request || outer) || {}) as Record<string, unknown>;
  const sessionId = String(raw.sessionId || raw.id || '').trim();
  if (!sessionId) return null;
  return {
    sessionId,
    messageId: String(raw.messageId || '').trim(),
    fromId: String(raw.fromId || '').trim(),
    toId: String(raw.toId || '').trim(),
    fileName: String(raw.fileName || raw.name || 'file'),
    fileSize: Number(raw.fileSize || raw.size || 0),
    fileType: String(raw.fileType || raw.type || ''),
    iv: String(raw.iv || ''),
    status: String(raw.status || ''),
    iceServers: Array.isArray(outer.iceServers) ? outer.iceServers as RTCIceServer[] : []
  };
}

function safeFileId(sessionId?: string) {
  return `p2p-${String(sessionId || '').replace(/[^\w.-]/g, '').slice(0, 80)}`;
}

function pendingKey(messageId?: string) {
  return `pending:${String(messageId || '')}`;
}

function pickIceServers(...sources: unknown[]) {
  for (const source of sources) {
    if (Array.isArray(source) && source.length > 0) return source as RTCIceServer[];
  }
  return [];
}

function p2pCancelledText(options: ManagerOptions) {
  return options.t('p2pFileCancelled');
}

function messageMetaRecord(message: ChatMessage): Record<string, unknown> {
  const raw = (message as ChatMessage & { meta?: unknown; meta_json?: unknown }).meta
    ?? (message as ChatMessage & { meta_json?: unknown }).meta_json;
  if (!raw) return {};
  if (typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw === 'string' && /^[{[]/.test(raw.trim())) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {}
  }
  return {};
}

function isAcceptedFriend(peer?: PeerRecord) {
  return !!peer && peer.type !== 'group' && peer.status === 'accepted';
}

export function shouldUseChatP2PFile(peer: PeerRecord | undefined, file: File) {
  return isAcceptedFriend(peer) && Number(file?.size || 0) > CHAT_P2P_FILE_THRESHOLD;
}

export function isChatP2PFileMessage(message: ChatMessage) {
  const record = messageMetaRecord(message);
  return record.source === 'chat-p2p-file' || record.p2p === true || record.transfer === 'chat-p2p-file';
}

export function createChatP2PFileManager(options: ManagerOptions) {
  const records = new Map<string, ChatP2PRecord>();

  function bindRecordToSession(record: ChatP2PRecord, session: ChatP2PSession) {
    if (record.sessionId && record.sessionId !== session.sessionId) records.delete(record.sessionId);
    records.delete(pendingKey(record.messageId || session.messageId));
    record.session = session;
    record.sessionId = session.sessionId;
    records.set(session.sessionId, record);
    return record;
  }

  function recordForSession(session: ChatP2PSession | null) {
    if (!session?.sessionId) return null;
    const record = records.get(session.sessionId) || records.get(pendingKey(session.messageId));
    return record ? bindRecordToSession(record, session) : null;
  }

  function findRecordByMessage(message: ChatMessage) {
    const meta = messageMetaRecord(message);
    const sessionId = String(meta.p2pSessionId || '').trim();
    const messageId = String(message.id || '').trim();
    if (sessionId && records.has(sessionId)) return records.get(sessionId);
    if (messageId && records.has(pendingKey(messageId))) return records.get(pendingKey(messageId));
    for (const record of records.values()) {
      if (record.messageId === messageId) return record;
    }
    return null;
  }

  function transferPercent(record: ChatP2PRecord) {
    const total = Number(record.totalBytes || record.encryptedSize || record.cipherBlob?.size || record.fileSize || 0);
    const bytes = Number(record.bytesTransferred || 0);
    if (total > 0) return Math.max(0, Math.min(99, Math.round((bytes / total) * 100)));
    return 0;
  }

  function transferMeta(record: ChatP2PRecord, phase = record.phase || 'transferring') {
    return {
      source: 'chat-p2p-file',
      p2p: true,
      p2pSessionId: record.sessionId || '',
      transfer: 'chat-p2p-file',
      direction: record.direction,
      phase,
      speedBps: Math.max(0, Math.round(Number(record.speedBps || 0))),
      bytesTransferred: Math.max(0, Math.round(Number(record.bytesTransferred || 0))),
      totalBytes: Math.max(0, Math.round(Number(record.totalBytes || record.encryptedSize || record.fileSize || 0)))
    };
  }

  function patchTransferMessage(record: ChatP2PRecord, status: ChatMessage['status'] = 'sending', phase = record.phase || 'transferring', extra: Partial<ChatMessage> & Record<string, unknown> = {}) {
    record.phase = phase as ChatP2PRecord['phase'];
    options.patchMessage(record.peerId, record.messageId, {
      status,
      uploadProgress: status === 'sent' ? 100 : transferPercent(record),
      meta: transferMeta(record, phase),
      ...extra
    } as Partial<ChatMessage> & Record<string, unknown>);
  }

  function updateTransferStats(record: ChatP2PRecord, bytes: number, total: number, phase: ChatP2PRecord['phase'] = 'transferring') {
    const now = Date.now();
    const safeBytes = Math.max(0, Number(bytes || 0));
    const safeTotal = Math.max(0, Number(total || 0));
    if (!record.startedAt) {
      record.startedAt = now;
      record.speedSampleAt = now;
      record.speedSampleBytes = safeBytes;
    }
    const elapsed = Math.max(0.001, (now - Number(record.speedSampleAt || now)) / 1000);
    if (elapsed >= 0.25 || (safeTotal > 0 && safeBytes >= safeTotal)) {
      const delta = Math.max(0, safeBytes - Number(record.speedSampleBytes || 0));
      const instant = delta / elapsed;
      record.speedBps = record.speedBps ? (record.speedBps * 0.65 + instant * 0.35) : instant;
      record.speedSampleAt = now;
      record.speedSampleBytes = safeBytes;
    }
    record.bytesTransferred = safeBytes;
    record.totalBytes = safeTotal || record.totalBytes || record.fileSize || 0;
    record.phase = phase;
    patchTransferMessage(record, 'sending', phase);
  }

  function emitWithAck(event: string, payload: Record<string, unknown>, timeoutMs = ACK_TIMEOUT_MS): Promise<Record<string, unknown>> {
    const socket = options.getSocket();
    if (!socket?.socket?.connected) return Promise.reject(new Error(options.t('offline')));
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error(options.t('p2pFileStartFailed')));
      }, timeoutMs);
      socket.emit(event, payload, (ack: unknown) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        const data = (ack || {}) as Record<string, unknown>;
        if (data.ok === false) reject(new Error(String(data.error || data.code || options.t('p2pFileStartFailed'))));
        else resolve(data);
      });
    });
  }

  function buildMessage(record: ChatP2PRecord, status = 'sent'): ChatMessage {
    const phase = status === 'failed' ? 'failed' : status === 'sent' ? 'done' : (record.phase || 'transferring');
    const message = {
      id: record.messageId,
      fromId: record.fromId,
      toId: record.toId,
      ts: record.messageTs || Date.now(),
      type: 'file',
      iv: record.iv,
      payload: null,
      plain: record.fileName,
      file: {
        id: safeFileId(record.sessionId),
        name: record.fileName,
        size: record.fileSize,
        type: record.fileType,
        mime: record.fileType,
        url: ''
      },
      meta: {
        source: 'chat-p2p-file',
        p2p: true,
        p2pSessionId: record.sessionId || '',
        transfer: 'chat-p2p-file',
        direction: record.direction,
        phase,
        speedBps: Math.max(0, Math.round(Number(record.speedBps || 0))),
        bytesTransferred: Math.max(0, Math.round(Number(record.bytesTransferred || 0))),
        totalBytes: Math.max(0, Math.round(Number(record.totalBytes || record.encryptedSize || record.fileSize || 0)))
      },
      uploadProgress: status === 'sent' ? 100 : transferPercent(record),
      status
    } as ChatMessage & Record<string, unknown>;
    return message;
  }

  function closeRecord(record?: ChatP2PRecord) {
    if (!record) return;
    try { record.channel?.close(); } catch {}
    try { record.pc?.close(); } catch {}
    if (record.connectTimer) window.clearTimeout(record.connectTimer);
    if (record.doneTimer) window.clearTimeout(record.doneTimer);
  }

  function resolveIncomingOnThisDevice(record?: ChatP2PRecord | null) {
    if (!record) return;
    record.terminal = true;
    closeRecord(record);
    if (record.sessionId) records.delete(record.sessionId);
    records.delete(pendingKey(record.messageId));
  }

  function failOutgoing(record: ChatP2PRecord, error: unknown) {
    if (record.terminal) return;
    record.terminal = true;
    closeRecord(record);
    options.patchMessage(record.peerId, record.messageId, {
      status: 'failed',
      uploadProgress: 0,
      error: error instanceof Error ? error.message : String(error || options.t('p2pFileFailed')),
      meta: transferMeta(record, 'failed')
    });
    options.notify(error instanceof Error ? error.message : String(error || options.t('p2pFileFailed')));
  }

  function failIncoming(record: ChatP2PRecord, error: unknown) {
    if (record.terminal) return;
    record.terminal = true;
    closeRecord(record);
    options.getSocket()?.emit('chat-p2p-file:error', { sessionId: record.sessionId, error: error instanceof Error ? error.message : String(error || 'failed') });
    patchTransferMessage(record, 'failed', 'failed', {
      uploadProgress: 0,
      error: error instanceof Error ? error.message : String(error || options.t('p2pFileFailed'))
    });
    options.notify(error instanceof Error ? error.message : String(error || options.t('p2pFileFailed')));
  }

  function markCancelled(record: ChatP2PRecord, notifyPeer = false) {
    if (record.terminal) return;
    record.terminal = true;
    record.phase = 'cancelled';
    try {
      if (record.channel?.readyState === 'open') {
        record.channel.send(JSON.stringify({ kind: 'cancel', sessionId: record.sessionId, messageId: record.messageId }));
      }
    } catch {}
    closeRecord(record);
    patchTransferMessage(record, 'failed', 'cancelled', {
      error: p2pCancelledText(options)
    });
    if (record.sessionId) records.delete(record.sessionId);
    records.delete(pendingKey(record.messageId));
    if (notifyPeer && record.sessionId) {
      emitWithAck('chat-p2p-file:cancel', { sessionId: record.sessionId, reason: 'cancelled' }, 5000).catch(() => undefined);
    }
    options.notify(p2pCancelledText(options));
  }

  async function waitForBufferedAmountLow(channel: RTCDataChannel) {
    while (channel.readyState === 'open' && channel.bufferedAmount > BUFFERED_AMOUNT_HIGH) {
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          channel.removeEventListener('bufferedamountlow', finish);
          resolve();
        };
        channel.bufferedAmountLowThreshold = BUFFERED_AMOUNT_LOW;
        channel.addEventListener('bufferedamountlow', finish, { once: true });
        window.setTimeout(finish, 160);
      });
    }
  }

  async function sendChannel(channel: RTCDataChannel, payload: string | ArrayBuffer) {
    if (channel.readyState !== 'open') throw new Error(options.t('p2pFileConnectionClosed'));
    await waitForBufferedAmountLow(channel);
    if (typeof payload === 'string') channel.send(payload);
    else channel.send(payload);
  }

  function createPeerConnection(record: ChatP2PRecord) {
    const pc = new RTCPeerConnection({ iceServers: record.iceServers || [] });
    record.pc = pc;
    pc.onicecandidate = (event) => {
      if (!event.candidate || !record.sessionId) return;
      options.getSocket()?.emit('chat-p2p-file:webrtc:ice', {
        sessionId: record.sessionId,
        candidate: event.candidate.toJSON ? event.candidate.toJSON() : event.candidate
      });
    };
    pc.onconnectionstatechange = () => {
      if (record.terminal) return;
      if (pc.connectionState === 'failed' || pc.connectionState === 'closed' || pc.connectionState === 'disconnected') {
        if (record.direction === 'outgoing' && record.waitingReceiverDone) return;
        if (record.direction === 'incoming' && record.receivedDone) return;
        if (record.direction === 'outgoing') failOutgoing(record, options.t('p2pFileConnectionFailed'));
        else failIncoming(record, options.t('p2pFileConnectionFailed'));
      }
    };
    return pc;
  }

  async function sendEncryptedBlob(record: ChatP2PRecord) {
    if (!record.channel || !record.cipherBlob) throw new Error(options.t('p2pFileMissingData'));
    const channel = record.channel;
    const blob = record.cipherBlob;
    record.totalBytes = blob.size;
    updateTransferStats(record, 0, blob.size, 'transferring');
    await sendChannel(channel, JSON.stringify({
      kind: 'meta',
      sessionId: record.sessionId,
      messageId: record.messageId,
      fileName: record.fileName,
      fileSize: record.fileSize,
      fileType: record.fileType,
      iv: record.iv,
      encryptedSize: blob.size,
      chunkSize: CHUNK_SIZE,
      totalChunks: Math.ceil(blob.size / CHUNK_SIZE)
    }));
    let offset = 0;
    let index = 0;
    while (offset < blob.size) {
      if (record.terminal) throw new Error(options.t('p2pFileFailed'));
      const chunk = await blob.slice(offset, offset + CHUNK_SIZE).arrayBuffer();
      await sendChannel(channel, JSON.stringify({ kind: 'chunk', sessionId: record.sessionId, messageId: record.messageId, index, offset, size: chunk.byteLength }));
      await sendChannel(channel, chunk);
      offset += chunk.byteLength;
      index += 1;
      updateTransferStats(record, offset, blob.size, 'transferring');
    }
    await sendChannel(channel, JSON.stringify({ kind: 'done', sessionId: record.sessionId, messageId: record.messageId, totalChunks: index }));
    record.waitingReceiverDone = true;
    updateTransferStats(record, blob.size, blob.size, 'finalizing');
    record.doneTimer = window.setTimeout(() => {
      if (!record.terminal) failOutgoing(record, options.t('p2pFileReceiveNotConfirmed'));
    }, FINAL_DONE_TIMEOUT_MS);
  }

  async function startSender(record: ChatP2PRecord) {
    const pc = createPeerConnection(record);
    record.phase = 'connecting';
    patchTransferMessage(record, 'sending', 'connecting');
    const channel = pc.createDataChannel(CHANNEL_LABEL, { ordered: true });
    channel.binaryType = 'arraybuffer';
    record.channel = channel;
    channel.onopen = () => {
      if (record.connectTimer) window.clearTimeout(record.connectTimer);
      record.phase = 'transferring';
      patchTransferMessage(record, 'sending', 'transferring');
      void sendEncryptedBlob(record).catch((error) => {
        options.getSocket()?.emit('chat-p2p-file:error', { sessionId: record.sessionId, error: error instanceof Error ? error.message : String(error) });
        failOutgoing(record, error);
      });
    };
    channel.onerror = () => failOutgoing(record, options.t('p2pFileConnectionFailed'));
    channel.onclose = () => {
      if (!record.terminal && !record.waitingReceiverDone) failOutgoing(record, options.t('p2pFileConnectionClosed'));
    };
    record.connectTimer = window.setTimeout(() => {
      if (record.channel?.readyState !== 'open') {
        options.getSocket()?.emit('chat-p2p-file:error', { sessionId: record.sessionId, error: 'connect_timeout' });
        failOutgoing(record, options.t('p2pFileStartFailed'));
      }
    }, CONNECT_TIMEOUT_MS);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await emitWithAck('chat-p2p-file:webrtc:offer', { sessionId: record.sessionId, offer: pc.localDescription || offer });
  }

  async function acceptIncoming(record: ChatP2PRecord) {
    const ack = await emitWithAck('chat-p2p-file:accept', { sessionId: record.sessionId });
    const session = sessionOf(ack);
    if (session) {
      record.session = session;
      record.iceServers = pickIceServers(session.iceServers, record.iceServers);
    }
    record.phase = 'connecting';
    record.totalBytes = record.encryptedSize || record.fileSize || 0;
    options.appendOrPatchMessage(record.peerId, buildMessage(record, 'sending'));
  }

  async function rejectIncoming(record: ChatP2PRecord) {
    record.terminal = true;
    await emitWithAck('chat-p2p-file:reject', { sessionId: record.sessionId }).catch(() => undefined);
  }

  function showIncoming(record: ChatP2PRecord) {
    const peer = options.getPeer(record.fromId);
    const peerName = (peer && 'nickname' in peer ? peer.nickname : '') || record.fromId;
    const ok = window.confirm(`${peerName} ${options.t('p2pFileIncomingPrompt')}:\n${record.fileName}`);
    if (record.terminal) return;
    if (ok) void acceptIncoming(record);
    else void rejectIncoming(record);
  }

  function handleIncoming(payload: unknown) {
    const session = sessionOf(payload);
    if (!session) return;
    const record: ChatP2PRecord = {
      direction: 'incoming',
      session,
      sessionId: session.sessionId,
      peerId: session.fromId,
      fromId: session.fromId,
      toId: session.toId,
      messageId: session.messageId,
      fileName: session.fileName,
      fileSize: session.fileSize,
      fileType: session.fileType,
      iv: session.iv,
      iceServers: pickIceServers(session.iceServers),
      totalBytes: session.fileSize || 0,
      phase: 'waiting',
      terminal: false
    };
    records.set(session.sessionId, record);
    showIncoming(record);
  }

  function handleAccepted(payload: unknown) {
    const session = sessionOf(payload);
    if (!session) return;
    const record = recordForSession(session);
    if (!record || record.direction !== 'outgoing') return;
    record.phase = 'connecting';
    patchTransferMessage(record, 'sending', 'connecting');
    void startSender(record).catch((error) => failOutgoing(record, error));
  }

  function handleOffer(payload: Record<string, unknown>) {
    const session = sessionOf(payload);
    if (!session || !payload.offer) return;
    const record = records.get(session.sessionId);
    if (!record) return;
    record.phase = 'connecting';
    record.iceServers = pickIceServers(session.iceServers, record.iceServers);
    patchTransferMessage(record, 'sending', 'connecting');
    void (async () => {
      const pc = createPeerConnection(record);
      pc.ondatachannel = (event) => {
        const channel = event.channel;
        channel.binaryType = 'arraybuffer';
        record.channel = channel;
        channel.onmessage = (messageEvent) => handleReceiverMessage(record, messageEvent);
        channel.onerror = () => failIncoming(record, options.t('p2pFileConnectionFailed'));
        channel.onclose = () => {
          if (!record.terminal && !record.receivedDone) failIncoming(record, options.t('p2pFileConnectionClosed'));
        };
      };
      await pc.setRemoteDescription(new RTCSessionDescription(payload.offer as RTCSessionDescriptionInit));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await emitWithAck('chat-p2p-file:webrtc:answer', { sessionId: record.sessionId, answer: pc.localDescription || answer });
    })().catch((error) => failIncoming(record, error));
  }

  function handleReceiverMessage(record: ChatP2PRecord, event: MessageEvent) {
    if (typeof event.data === 'string') {
      let message: Record<string, unknown> | null = null;
      try { message = JSON.parse(event.data) as Record<string, unknown>; } catch {}
      if (!message || message.sessionId !== record.sessionId) return;
      if (message.kind === 'meta') {
        record.messageId = String(message.messageId || record.messageId);
        record.fileName = String(message.fileName || record.fileName);
        record.fileSize = Number(message.fileSize || record.fileSize);
        record.fileType = String(message.fileType || record.fileType);
        record.iv = String(message.iv || record.iv);
        record.encryptedSize = Number(message.encryptedSize || message.totalBytes || record.encryptedSize || record.fileSize || 0);
        record.totalBytes = record.encryptedSize || record.fileSize || 0;
        record.chunks = [];
        record.receivedBytes = 0;
        updateTransferStats(record, 0, record.totalBytes, 'transferring');
        return;
      }
      if (message.kind === 'done') {
        record.receivedDone = true;
        void finalizeIncoming(record);
      }
      if (message.kind === 'cancel') {
        markCancelled(record, false);
      }
      return;
    }
    const push = async (buffer: ArrayBuffer) => {
      record.chunks = record.chunks || [];
      record.chunks.push(buffer);
      record.receivedBytes = Number(record.receivedBytes || 0) + buffer.byteLength;
      updateTransferStats(record, record.receivedBytes, record.totalBytes || record.fileSize || 0, 'transferring');
    };
    if (event.data instanceof ArrayBuffer) void push(event.data);
    else if (event.data instanceof Blob) void event.data.arrayBuffer().then(push).catch((error) => failIncoming(record, error));
    else if (ArrayBuffer.isView(event.data)) {
      const view = event.data;
      const copy = new Uint8Array(view.byteLength);
      copy.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
      void push(copy.buffer);
    }
  }

  async function finalizeIncoming(record: ChatP2PRecord) {
    try {
      const encryptedBlob = new Blob(record.chunks || [], { type: 'application/octet-stream' });
      const plainBlob = await options.decryptIncomingBlob(record, encryptedBlob);
      const message = buildMessage(record, 'sent');
      options.saveReceivedBlob(message, record.peerId, plainBlob);
      updateTransferStats(record, record.totalBytes || record.receivedBytes || plainBlob.size, record.totalBytes || record.receivedBytes || plainBlob.size, 'finalizing');
      record.terminal = true;
      closeRecord(record);
      options.appendOrPatchMessage(record.peerId, buildMessage(record, 'sent'));
      await emitWithAck('chat-p2p-file:done', { sessionId: record.sessionId, messageId: record.messageId, bytes: plainBlob.size }, 8000).catch(() => undefined);
      options.notify(`${options.t('p2pFileReceived')}: ${record.fileName}`);
    } catch (error) {
      failIncoming(record, error);
    }
  }

  function handleAnswer(payload: Record<string, unknown>) {
    const sessionId = String(payload.sessionId || (payload.session as Record<string, unknown> | undefined)?.sessionId || '');
    const record = records.get(sessionId);
    if (!record?.pc || !payload.answer) return;
    void record.pc.setRemoteDescription(new RTCSessionDescription(payload.answer as RTCSessionDescriptionInit)).catch((error) => failOutgoing(record, error));
  }

  function handleIce(payload: Record<string, unknown>) {
    const sessionId = String(payload.sessionId || (payload.session as Record<string, unknown> | undefined)?.sessionId || '');
    const record = records.get(sessionId);
    if (!record?.pc || !payload.candidate) return;
    void record.pc.addIceCandidate(new RTCIceCandidate(payload.candidate as RTCIceCandidateInit)).catch(() => undefined);
  }

  function handleDone(payload: unknown) {
    const session = sessionOf(payload);
    const record = recordForSession(session);
    if (!record || record.direction !== 'outgoing' || record.doneConfirmed) return;
    record.doneConfirmed = true;
    record.terminal = true;
    record.waitingReceiverDone = false;
    record.bytesTransferred = Math.max(
      Number(record.bytesTransferred || 0),
      Number(record.totalBytes || record.encryptedSize || record.cipherBlob?.size || record.fileSize || 0)
    );
    closeRecord(record);
    const message = buildMessage(record, 'sent');
    (message as ChatMessage & { uploadProgress?: number }).uploadProgress = 100;
    options.appendOrPatchMessage(record.peerId, message);
    if (record.file) options.saveReceivedBlob(message, record.peerId, record.file);
    options.sendChatMessage(message);
    options.notify(`${options.t('p2pFileSent')}: ${record.fileName}`);
  }

  function handleError(payload: unknown) {
    const session = sessionOf(payload);
    const record = recordForSession(session);
    if (!record) return;
    if (record.direction === 'outgoing') failOutgoing(record, (payload as Record<string, unknown>).error || options.t('p2pFileFailed'));
    else failIncoming(record, (payload as Record<string, unknown>).error || options.t('p2pFileFailed'));
  }

  function handleRejected(payload: unknown) {
    const session = sessionOf(payload);
    const record = recordForSession(session);
    if (!record) return;
    if (record.direction === 'outgoing') failOutgoing(record, options.t('p2pFileRejected'));
    else resolveIncomingOnThisDevice(record);
  }

  function handleCancelled(payload: unknown) {
    const session = sessionOf(payload);
    const record = recordForSession(session);
    if (record) markCancelled(record, false);
  }

  function handleRequestResolved(payload: unknown) {
    const session = sessionOf(payload);
    const record = recordForSession(session);
    if (record?.direction === 'incoming') resolveIncomingOnThisDevice(record);
  }

  return {
    attach(socket: Socket) {
      if ((socket as Socket & { __skychatChatP2PFileHandlers?: boolean }).__skychatChatP2PFileHandlers) return;
      (socket as Socket & { __skychatChatP2PFileHandlers?: boolean }).__skychatChatP2PFileHandlers = true;
      socket.on('chat-p2p-file:incoming', handleIncoming);
      socket.on('chat-p2p-file:request:resolved', handleRequestResolved);
      socket.on('chat-p2p-file:accepted', handleAccepted);
      socket.on('chat-p2p-file:rejected', handleRejected);
      socket.on('chat-p2p-file:cancelled', handleCancelled);
      socket.on('chat-p2p-file:webrtc:offer', (payload) => handleOffer(payload as Record<string, unknown>));
      socket.on('chat-p2p-file:webrtc:answer', (payload) => handleAnswer(payload as Record<string, unknown>));
      socket.on('chat-p2p-file:webrtc:ice', (payload) => handleIce(payload as Record<string, unknown>));
      socket.on('chat-p2p-file:done', handleDone);
      socket.on('chat-p2p-file:error', handleError);
    },
    async send(peerId: string, file: File, cipherBlob: Blob, iv: string, message: ChatMessage) {
      const record: ChatP2PRecord = {
        direction: 'outgoing',
        peerId,
        fromId: message.fromId,
        toId: peerId,
        messageId: message.id,
        messageTs: message.ts,
        file,
        cipherBlob,
        fileName: file.name || 'file',
        fileSize: Number(file.size || 0),
        fileType: file.type || '',
        iv,
        totalBytes: cipherBlob?.size || file.size || 0,
        phase: 'waiting',
        terminal: false
      };
      try {
        records.set(pendingKey(record.messageId), record);
        const ack = await emitWithAck('chat-p2p-file:request', {
          toId: peerId,
          messageId: message.id,
          fileName: file.name || 'file',
          fileSize: Number(file.size || 0),
          fileType: file.type || '',
          iv
        });
        const session = sessionOf(ack);
        if (!session) throw new Error(options.t('p2pFileStartFailed'));
        bindRecordToSession(record, session);
        record.iceServers = pickIceServers(session.iceServers, (ack as Record<string, unknown>).iceServers);
        options.patchMessage(peerId, message.id, {
          meta: {
            source: 'chat-p2p-file',
            p2p: true,
            p2pSessionId: session.sessionId,
            transfer: 'chat-p2p-file',
            direction: 'outgoing',
            phase: 'waiting',
            speedBps: 0,
            bytesTransferred: 0,
            totalBytes: record.totalBytes || 0
          }
        });
        options.notify(options.t('p2pFileWaitingAccept'));
      } catch (error) {
        records.delete(pendingKey(record.messageId));
        failOutgoing(record, error);
      }
    },
    async cancel(message: ChatMessage) {
      const record = findRecordByMessage(message);
      if (record) {
        markCancelled(record, true);
        return true;
      }
      const sessionId = String(messageMetaRecord(message).p2pSessionId || '').trim();
      if (!sessionId) return false;
      await emitWithAck('chat-p2p-file:cancel', { sessionId, reason: 'cancelled' }, 5000).catch(() => undefined);
      return true;
    }
  };
}
